import type { Messages } from './en.js';

export const ptBR: Messages = {
  app: {
    name: 'Vammo Work Tracker',
    fixtureBanner: 'Ambiente de fixtures — dados de exemplo, sem integrações reais e sem mensagens reais.',
    skipToContent: 'Ir para o conteúdo',
  },
  nav: {
    myWork: 'Meu trabalho',
    requests: 'Solicitações',
    work: 'Trabalho',
    projects: 'Projetos',
    teams: 'Times',
    reports: 'Relatórios',
    admin: 'Administração',
    settings: 'Configurações',
    newRequest: 'Nova solicitação',
    newWork: 'Novo item de trabalho',
    search: 'Buscar',
    searchPlaceholder: 'Busque trabalho e solicitações ou digite uma chave…',
    notifications: 'Notificações',
    collapse: 'Recolher menu',
    expand: 'Expandir menu',
    openMenu: 'Abrir navegação',
    signOut: 'Sair',
    theme: 'Tema',
    themeLight: 'Claro',
    themeDark: 'Escuro',
    themeSystem: 'Sistema',
    language: 'Idioma',
    shortcuts: 'Atalhos de teclado',
    tutorial: 'Tutorial',
  },
  repoPicker: {
    workTitle: 'Repositórios vinculados',
    workHint: 'Vincular repositórios adiciona evidências para o trabalho futuro. Episódios concluídos mantêm sua base original.',
    projectTitle: 'Repositórios do projeto',
    projectHint: 'A reconciliação e a cobertura de captura incluem os repositórios anexados aqui.',
    search: 'Buscar repositórios',
    noneSelected: 'Nenhum repositório selecionado.',
    remove: 'Remover {{name}}',
    archived: 'Arquivado',
    edit: 'Editar repositórios',
  },
  translationReport: {
    action: 'Reportar',
    title: 'Reportar esta tradução',
    hint: 'Conte o que está errado. O texto original nunca é alterado.',
    comment: 'O que está errado? (opcional)',
    regenerate: 'Traduzir novamente',
    submit: 'Enviar relato',
    thanks: 'Obrigado, o relato foi registrado',
  },
  languageCorrection: {
    label: 'Idioma original',
    hint: 'Corrija se o texto foi detectado no idioma errado; as traduções são recriadas.',
  },
  conflict: {
    title: 'Outra pessoa salvou antes',
    hint: 'Compare sua alteração com a versão atual e escolha qual manter.',
    field: 'Campo',
    mine: 'Sua alteração',
    current: 'Versão atual',
    applyMine: 'Aplicar minha alteração',
    keepCurrent: 'Manter a versão atual',
    fields: { title: 'Título', descriptionMarkdown: 'Descrição', acceptanceCriteria: 'Critérios de aceitação', priority: 'Prioridade', assigneeId: 'Responsável', labels: 'Etiquetas', type: 'Tipo', originalLocale: 'Idioma original', repositoryIds: 'Repositórios', problemMarkdown: 'Problema', expectedOutcome: 'Resultado esperado' },
  },
  common: {
    removeItem: 'Remover {{name}}',
    save: 'Salvar',
    saving: 'Salvando…',
    cancel: 'Cancelar',
    close: 'Fechar',
    create: 'Criar',
    edit: 'Editar',
    delete: 'Excluir',
    archive: 'Arquivar',
    confirm: 'Confirmar',
    submit: 'Enviar',
    back: 'Voltar',
    next: 'Avançar',
    retry: 'Tentar de novo',
    reload: 'Recarregar',
    loading: 'Carregando…',
    loadMore: 'Carregar mais',
    none: 'Nenhum',
    unassigned: 'Sem responsável',
    unknown: 'Desconhecido',
    optional: 'opcional',
    required: 'Obrigatório',
    yes: 'Sim',
    no: 'Não',
    all: 'Todos',
    me: 'Eu',
    filter: 'Filtrar',
    clearFilters: 'Limpar filtros',
    sort: 'Ordenar',
    openFullPage: 'Abrir página completa',
    copyLink: 'Copiar link',
    linkCopied: 'Link copiado',
    viewOriginal: 'Ver original',
    viewTranslation: 'Ver tradução',
    machineTranslated: 'Tradução automática do {{language}}',
    translating: 'Traduzindo…',
    translationFailed: 'Tradução indisponível; exibindo o original.',
    reportTranslation: 'Reportar tradução',
    noResults: 'Nenhum resultado',
    comingSoon: 'Ainda não disponível',
    reason: 'Motivo',
    reasonPlaceholder: 'Explique a decisão',
    created: 'Criado',
    updated: 'Atualizado',
    by: 'por {{name}}',
    you: 'você',
    changesSaved: 'Alterações salvas',
    actionFailed: 'A ação falhou',
    count_one: '{{count}} item',
    count_other: '{{count}} itens',
    preview: 'Pré-visualizar',
    write: 'Escrever',
    markdownHint: 'Markdown suportado: **negrito**, _itálico_, `código`, listas e links.',
    unsavedChanges: 'Você tem alterações não salvas. Salve ou cancele primeiro.',
    discard: 'Descartar',
    keepEditing: 'Continuar editando',
  },
  errors: {
    title: 'Algo deu errado',
    notFound: 'Não encontrado, ou você não tem acesso.',
    forbidden: 'Você não tem permissão para fazer isso.',
    unauthenticated: 'Sua sessão terminou. Entre novamente para continuar.',
    conflict: 'Outra pessoa alterou isto enquanto isso. Revise a versão mais recente e reaplique sua edição.',
    staleGeneration: 'Existe uma entrega mais recente. Revise o estado atual antes de responder.',
    rateLimited: 'Muitas solicitações. Aguarde um momento.',
    unavailable: 'Este recurso não está disponível agora.',
    offline: 'Você parece estar offline. As alterações não serão salvas até a conexão voltar.',
    validation: 'Corrija os campos destacados.',
    generic: 'Erro inesperado. Referência: {{correlationId}}',
  },
  auth: {
    signIn: 'Entrar com a Vammo',
    signingIn: 'Redirecionando para o login…',
    errorTitle: 'O login não foi concluído',
    errorState: 'O link de login expirou ou já foi usado. Comece de novo.',
    errorSuspended: 'Sua conta está suspensa. Fale com um administrador.',
    errorGeneric: 'Não conseguimos verificar seu login corporativo.',
    tryAgain: 'Tentar de novo',
  },
  status: {
    request: {
      DRAFT: 'Rascunho',
      SUBMITTED: 'Enviada',
      NEEDS_INFORMATION: 'Precisa de informações',
      ACCEPTED: 'Aceita',
      REJECTED: 'Recusada',
      DUPLICATE: 'Duplicada',
      RESOLVED: 'Resolvida',
      NEEDS_ATTENTION: 'Requer atenção',
      CANCELED: 'Cancelada',
    },
    phase: {
      TODO: 'A fazer',
      IN_PROGRESS: 'Em andamento',
      IN_REVIEW: 'Em revisão',
      INTEGRATED: 'Integrado',
      DONE: 'Concluído',
      CANCELED: 'Cancelado',
    },
    priority: { URGENT: 'Urgente', HIGH: 'Alta', NORMAL: 'Normal', LOW: 'Baixa' },
    workType: {
      FEATURE: 'Funcionalidade',
      BUG: 'Bug',
      MAINTENANCE: 'Manutenção',
      INFRASTRUCTURE: 'Infraestrutura',
      RESEARCH: 'Pesquisa',
      ANALYSIS: 'Análise',
      OPERATIONS: 'Operações',
      REVIEW: 'Revisão',
      OTHER: 'Outro',
      EPIC: 'Épico',
    },
    policy: { PRODUCTION: 'Entrega em produção', DELIVERABLE: 'Entregável', REVIEWED_DELIVERABLE: 'Entregável revisado' },
    policyHelp: {
      PRODUCTION: 'Concluído apenas quando todas as mudanças necessárias estiverem verificadas em produção.',
      DELIVERABLE: 'Concluído quando o responsável registra o resultado frente aos critérios.',
      REVIEWED_DELIVERABLE: 'Concluído quando um revisor independente aprova o resultado.',
    },
    delivery: { UNKNOWN: 'Entrega desconhecida', NOT_DEPLOYED: 'Não implantado', PARTIAL: 'Implantado parcialmente', DEPLOYED: 'Implantado', REVERTED: 'Revertido' },
    association: { CONFIRMED: 'Confirmado', PROVISIONAL: 'Provisório' },
    visibility: { COMPANY: 'Empresa', RESTRICTED: 'Restrito' },
    visibilityHelp: { COMPANY: 'Visível para todos os colaboradores da Vammo com login.', RESTRICTED: 'Visível apenas para as pessoas, times e grupos selecionados (e administradores).' },
    basis: { AGENT_REPORTED: 'Relatado por agente', HUMAN_REPORTED: 'Relatado', PROVIDER_VERIFIED: 'Verificado', IMPORTED_SOURCE: 'Importado', FIXTURE: 'Fixture' },
    completionBasis: { VERIFIED_PRODUCTION: 'Verificado em produção', DELIVERABLE: 'Entregável registrado', REVIEWED_DELIVERABLE: 'Revisado e aprovado', IMPORTED_SOURCE: 'Concluído no Jira (importado)' },
    confirmation: { UNCONFIRMED: 'Aguardando sua confirmação', CONFIRMED: 'Confirmado como resolvido', PERSISTS: 'Ainda acontece' },
    fulfillment: {
      NOT_STARTED: 'Não iniciado',
      IN_PROGRESS: 'Em andamento',
      IN_REVIEW: 'Em revisão',
      AWAITING_DEPLOYMENT: 'Aguardando implantação em produção',
      AWAITING_ACTIVATION: 'Aguardando ativação',
      DELIVERED: 'Entregue',
      NONE_LINKED: 'Ainda não planejado',
    },
    channel: { WEB: 'Web', SLACK: 'Slack', MCP: 'Assistente de IA', API: 'API', EMAIL: 'E-mail', JIRA_IMPORT: 'Importação do Jira', SYSTEM: 'Sistema' },
    scope: { NONE: 'Não se aplica', AWAITING_SCOPE: 'Aguardando escopo de entrega', UNCONFIRMED: 'Escopo não confirmado', CONFIRMED: 'Escopo confirmado' },
    milestone: { START: 'Iniciado', SCOPE_CHANGE: 'Escopo alterado', BLOCKER: 'Bloqueio', VERIFICATION: 'Verificação', PR_UPDATE: 'Pull request', HANDOFF: 'Passagem', SESSION_END: 'Sessão encerrada', NOTE: 'Nota' },
    client: { CLAUDE_CODE: 'Claude Code', CODEX: 'Codex', CLAUDE: 'Claude', CHATGPT: 'ChatGPT', CLAWBOT: 'Clawbot (Slack)', OTHER: 'Outro cliente' },
  },
  actions: {
    request: {
      submit: 'Enviar solicitação',
      cancel: 'Cancelar solicitação',
      ask_information: 'Pedir informações',
      answer_information: 'Enviar resposta',
      accept: 'Aceitar',
      reject: 'Recusar',
      mark_duplicate: 'Marcar como duplicada',
      fulfill: 'Marcar como atendida',
      report_persistence: 'Informar que continua',
      fulfillment_invalidated: 'Reabrir para atenção',
      accept_correction: 'Aceitar correção',
      fulfill_after_review: 'Resolver após revisão',
      reconsider: 'Reconsiderar',
    },
    work: {
      start: 'Iniciar',
      request_review: 'Pedir revisão',
      request_changes: 'Pedir ajustes',
      integration_observed: 'Integrado',
      production_verified: 'Verificado em produção',
      complete_deliverable: 'Concluir',
      approve_deliverable: 'Aprovar',
      cancel: 'Cancelar trabalho',
      reopen: 'Reabrir',
      coverage_lost: 'Cobertura perdida',
    },
  },
  myWork: {
    title: 'Meu trabalho',
    greeting: 'Olá, {{name}}',
    subtitle: 'O que precisa da sua atenção hoje.',
    assigned: 'Atribuído a mim',
    assignedEmpty: 'Nada atribuído a você agora.',
    myRequests: 'Minhas solicitações',
    myRequestsEmpty: 'Você ainda não registrou solicitações.',
    needsMe: 'Aguardando você',
    needsMeEmpty: 'Nada está aguardando você.',
    blocked: 'Bloqueado',
    triageInbox: 'Caixa de triagem',
    updates: 'Atualizações recentes',
    updatesEmpty: 'Ainda não há atualizações.',
    following: 'Seguindo',
    followingEmpty: 'Siga trabalhos ou solicitações para vê-los aqui.',
    overdue: 'Atrasados',
    overdueEmpty: 'Nada seu está atrasado.',
    dueSoon: 'Vencem nos próximos 7 dias',
    dueSoonEmpty: 'Nada seu vence nesta semana.',
    stats: { open: 'Trabalho aberto', inReview: 'Em revisão', blocked: 'Bloqueados', attention: 'Solicitações que requerem atenção', overdue: 'Atrasados' },
  },
  requests: {
    move: { noProject: 'Sem projeto', title: 'Mover para {{name}}', hint: 'Mover muda quem pode ler esta solicitação, seus comentários e arquivos.', restrictedAfter: 'Após mover, a solicitação ficará restrita ao público do projeto.', companyAfter: 'Após mover, a solicitação ficará visível para toda a empresa.', losesAccess: 'Estas pessoas perderiam acesso:', nobodyLoses: 'Ninguém envolvido perde acesso.', reassignFirst: 'O responsável perderia acesso. Reatribua a solicitação primeiro.', confirm: 'Mover solicitação' },
    activation: { pending: 'Ativação pendente:', observed: 'Ativação observada por {{name}} em {{time}}', notRequired: 'Não requer uma ativação separada.', require: 'Exigir ativação', record: 'Registrar ativação', waive: 'Dispensar ativação', requireHint: 'Use quando implantar não basta, por exemplo quando um feature flag precisa ser ligado. A solicitação fica aceita até registrar a ativação.', recordHint: 'Registre quem observou a mudança ativa e a evidência. Fica auditado.', waiveHint: 'A ativação deixará de ser obrigatória. O motivo fica auditado.', noteLabel: 'O que precisa ser ativado?', evidenceLabel: 'Evidência', reasonLabel: 'Motivo' },
    duplicates: { title: 'Possíveis duplicatas', hint: 'Solicitações semelhantes que você pode ver. Ao marcar, cada solicitante continua informado.', mark: 'Marcar como duplicata desta' },
    title: 'Solicitações',
    subtitle: 'Problemas, ideias e pedidos de toda a Vammo.',
    tabs: { inbox: 'Entrada', mine: 'Minhas', attention: 'Requer atenção', following: 'Seguindo', all: 'Todas', snoozed: 'Adiadas' },
    empty: 'Nenhuma solicitação aqui.',
    emptyInbox: 'A caixa de entrada está vazia. Novas solicitações aparecem aqui para triagem.',
    columns: { key: 'Chave', title: 'Título', status: 'Status', priority: 'Prioridade', author: 'Solicitante', assignee: 'Responsável', team: 'Time', updated: 'Atualizado', source: 'Origem' },
    snooze: {
      action: 'Adiar',
      title: 'Adiar solicitação',
      hint: 'Sai da caixa de triagem e volta sozinha no momento escolhido. Qualquer mudança de status a traz de volta antes.',
      until: 'Adiar até',
      tomorrow: 'Amanhã de manhã',
      nextWeek: 'Na próxima segunda',
      inTwoWeeks: 'Em duas semanas',
      custom: 'Escolher uma data',
      badge: 'Adiada até {{time}}',
      wake: 'Reativar agora',
      snoozedToast: 'Adiada até {{time}}',
      wokenToast: 'De volta à caixa de entrada',
    },
    detail: {
      problem: 'Problema',
      currentBehavior: 'O que acontece hoje',
      expectedOutcome: 'Resultado esperado',
      impact: 'Impacto',
      impactUnknown: 'Impacto ainda desconhecido',
      reproduction: 'Como reproduzir',
      affectedArea: 'Área afetada',
      progress: 'Progresso',
      decisions: 'Histórico de decisões',
      comments: 'Comentários',
      linkedWork: 'Trabalho vinculado',
      linkedWorkEmpty: 'Nenhum trabalho vinculado ainda.',
      required: 'Obrigatório',
      optional: 'Opcional',
      hiddenWork: 'Parte do trabalho vinculado é restrita e não é exibida.',
      pendingQuestion: 'O time perguntou',
      answerPlaceholder: 'Responda à pergunta…',
      duplicateOf: 'Duplicada de',
      decisionReason: 'Nota da decisão',
      source: 'Origem',
      triage: 'Triagem',
      triageTeam: 'Time de triagem',
      followers: 'Seguir',
      following: 'Seguindo',
      linkWork: 'Vincular trabalho',
      linkWorkSearch: 'Busque trabalho por chave ou título',
      unlink: 'Desvincular',
    },
    confirm: {
      fromEmail: 'Escolha sua resposta abaixo. Nada é registrado até você clicar.',
      title: 'Isto foi resolvido para você?',
      body: 'O time marcou esta solicitação como resolvida. Diga se funcionou para você.',
      confirmed: 'Sim, está resolvido',
      persists: 'Não, ainda acontece',
      commentPlaceholder: 'Adicione detalhes (opcional)',
      thanks: 'Obrigado por confirmar.',
      reported: 'Obrigado — o time vai revisar novamente.',
      generation: 'Entrega nº {{generation}}',
    },
    dialogs: {
      rejectTitle: 'Recusar solicitação',
      duplicateTitle: 'Marcar como duplicada',
      duplicateSearch: 'Encontre a solicitação principal',
      askTitle: 'Pedir informações',
      askPlaceholder: 'O que você precisa saber?',
      fulfillTitle: 'Marcar como atendida',
      fulfillDirect: 'Descreva o resultado entregue',
      reviewTitle: 'Resolver após revisão',
      reviewPlaceholder: 'Explique por que o resultado atual agora atende à solicitação',
      correctionTitle: 'Aceitar correção',
      correctionWork: 'Item de trabalho corretivo (opcional)',
      cancelTitle: 'Cancelar solicitação',
      reconsiderTitle: 'Reconsiderar solicitação',
    },
  },
  comments: {
    react: 'Adicionar reação',
    summary: { action: 'Resumir a discussão', title: 'Resumo com IA', again: 'Gerar de novo', note: 'Feito com IA a partir dos {{count}} comentários que você pode ver. Pode errar; a fonte é a conversa.' },
    reactedBy: '{{names}} reagiu com {{reaction}}',
    reactions: { THUMBS_UP: 'joinha', THUMBS_DOWN: 'polegar para baixo', LAUGH: 'risada', HOORAY: 'comemoração', CONFUSED: 'confusão', HEART: 'coração', ROCKET: 'foguete', EYES: 'olhos' },
    mentionPeople: 'Mencionar alguém',
    mentionHint: 'Digite @ para mencionar alguém; a pessoa recebe uma notificação.',
  },
  labels: {
    title: 'Etiquetas',
    manage: 'Gerenciar etiquetas…',
    hint: 'Dê a cada etiqueta uma cor e uma descrição. As que não têm aparecem em cinza; excluir uma só remove a cor, nunca a etiqueta dos itens de trabalho.',
    name: 'Etiqueta',
    color: 'Cor',
    description: 'Descrição (opcional)',
    add: 'Salvar etiqueta',
    saved: 'Etiqueta salva',
    delete: 'Excluir a cor de {{name}}',
    empty: 'Nenhuma etiqueta tem cor ainda.',
    colors: { gray: 'Cinza', red: 'Vermelho', orange: 'Laranja', amber: 'Amarelo', green: 'Verde', teal: 'Verde-azulado', blue: 'Azul', indigo: 'Índigo', purple: 'Roxo', pink: 'Rosa' },
  },
  inbox: {
    title: 'Caixa de entrada',
    subtitle: 'Novidades do trabalho e das solicitações em que você participa.',
    tabs: { inbox: 'Todas', mentions: 'Menções', unread: 'Não lidas', snoozed: 'Adiadas', archived: 'Arquivadas' },
    empty: { inbox: 'Você está em dia.', mentions: 'Ninguém mencionou você ainda.', unread: 'Nada não lido.', snoozed: 'Nada adiado.', archived: 'Nada arquivado.' },
    markAllRead: 'Marcar tudo como lido',
    allRead: 'Tudo marcado como lido',
    archiveRead: 'Arquivar lidas',
    archivedRead: 'Notificações lidas arquivadas',
    markRead: 'Marcar como lida',
    archive: 'Arquivar',
    unarchive: 'Voltar para a caixa',
    snooze: 'Adiar',
    snoozeFor: { day: 'Até amanhã', threeDays: 'Por 3 dias', week: 'Por uma semana' },
    snoozed: 'Adiada',
    unsnooze: 'Trazer agora',
    snoozedUntil: 'adiada até {{time}}',
    unread: 'Não lida',
    unreadCount_one: '{{count}} não lida',
    unreadCount_other: '{{count}} não lidas',
    actions: 'Ações da notificação',
  },
  templates: {
    use: 'Começar de um modelo…',
    manage: 'Gerenciar',
    manageWork: 'Modelos de trabalho',
    manageRequest: 'Modelos de solicitação',
    saveAs: 'Salvar como modelo',
    saveTitle: 'Salvar como modelo',
    saveHintWork: 'Salva o título, tipo, prioridade, descrição, critérios, etiquetas e subitens para o próximo item começar deles.',
    saveHintRequest: 'Salva o texto da solicitação para a próxima começar dele.',
    name: 'Nome do modelo',
    description: 'Descrição (opcional)',
    onlyThisProject: 'Oferecer só neste projeto',
    saved: 'Modelo salvo',
    everywhere: 'Todos os projetos',
    remove: 'Remover o modelo {{name}}',
    empty: 'Ainda não há modelos. Salve um a partir de um formulário ou de um item de trabalho.',
    subItems_one: 'Cria {{count}} subitem',
    subItems_other: 'Cria {{count}} subitens',
  },
  intake: {
    title: 'Nova solicitação',
    subtitle: 'Descreva o que está acontecendo. Você não precisa saber como resolver.',
    tabs: { assistant: 'Guiado', form: 'Formulário' },
    assistantIntro: 'Conte o problema ou a ideia com suas palavras. Vou perguntar só o que faltar.',
    assistantPlaceholder: 'Digite sua resposta…',
    assistantUnavailable: 'O assistente está indisponível agora. Use o formulário; tudo fica salvo.',
    assistant: {
      ack: 'Obrigado, salvei isso no rascunho.',
      askProblem: 'O que está acontecendo, e onde?',
      askExpected: 'O que deveria acontecer?',
      askImpact: 'Quem é afetado e quanto isso importa? Tudo bem dizer que não sabe.',
      askReproduction: 'Se for um bug: como alguém consegue ver o problema acontecer, e com que frequência?',
      ready: 'Já tenho o que a triagem precisa. Revise o resumo e envie quando estiver certo.',
      fallback: 'O assistente está indisponível, então salvei sua resposta como está.',
    },
    fields: {
      title: 'Título curto',
      titlePlaceholder: 'ex.: O app não mostra o histórico de trocas',
      problem: 'Qual é o problema?',
      problemPlaceholder: 'Descreva a situação e quem é afetado',
      currentBehavior: 'O que acontece hoje?',
      expectedOutcome: 'O que deveria acontecer?',
      impact: 'Impacto',
      impactPlaceholder: 'Quem ou o que é afetado, e quanto?',
      impactUnknown: 'Ainda não sei o impacto',
      reproduction: 'Passos para reproduzir (para bugs)',
      affectedArea: 'Área ou produto afetado',
      language: 'Escrito em',
      visibility: 'Quem pode ver',
      team: 'Encaminhar ao time',
      teamAuto: 'Deixar a triagem decidir',
      priority: 'Prioridade',
      evidence: 'Anexos',
    },
    duplicates: 'Solicitações possivelmente relacionadas',
    duplicatesHelp: 'Verifique se alguma delas já cobre sua solicitação.',
    review: 'Revisar e enviar',
    reviewHelp: 'Confira o resumo abaixo. Você ainda pode editar antes de enviar.',
    missing: 'Ainda falta: {{fields}}',
    saved: 'Rascunho salvo',
    submitted: 'Solicitação {{key}} enviada',
    draftNote: 'Rascunhos são privados até você enviá-los.',
  },
  work: {
    title: 'Trabalho',
    subtitle: 'Tudo em andamento entre times, projetos e repositórios.',
    views: { list: 'Lista', board: 'Quadro' },
    empty: 'Nenhum trabalho corresponde a esses filtros.',
    columns: { key: 'Chave', title: 'Título', phase: 'Fase', priority: 'Prioridade', type: 'Tipo', assignee: 'Responsável', project: 'Projeto', team: 'Time', updated: 'Atualizado', delivery: 'Entrega', labels: 'Etiquetas', created: 'Criado', owner: 'Dono', start: 'Início', due: 'Prazo' },
    filters: { phase: 'Fase', type: 'Tipo', priority: 'Prioridade', assignee: 'Responsável', project: 'Projeto', team: 'Time', blocked: 'Somente bloqueados', provisional: 'Somente provisórios', includeCanceled: 'Incluir cancelados', label: 'Etiqueta', archived: 'Somente arquivados', due: 'Prazo', dueOverdue: 'Atrasados', dueSoon: 'Vencem nos próximos 7 dias', dueNone: 'Sem prazo' },
    bulk: { selected: '{{count}} selecionados', setPriority: 'Definir prioridade', assign: 'Atribuir', addLabel: 'Adicionar etiqueta', clear: 'Limpar seleção', setProject: 'Mover para projeto', noProject: 'Sem projeto', setTeam: 'Definir time', removeLabel: 'Remover etiqueta', labelPlaceholder: 'Etiqueta', archive: 'Arquivar', restore: 'Restaurar', setDue: 'Definir prazo', clearDue: 'Remover prazo' },
    columnsMenu: { title: 'Colunas', reset: 'Mostrar colunas padrão' },
    export: { title: 'Exportar', csv: 'Arquivo CSV', xlsx: 'Arquivo do Excel (.xlsx)', hint: 'Baixa os filtros atuais, até 5000 linhas.' },
    groupBy: 'Agrupar',
    groupNone: 'Sem agrupar',
    lanes: 'Linhas',
    lanesNone: 'Sem linhas',
    groupFields: { phase: 'Fase', priority: 'Prioridade', assignee: 'Responsável', project: 'Projeto', team: 'Time', type: 'Tipo', label: 'Etiqueta' },
    groups: { noProject: 'Sem projeto', noTeam: 'Sem time', noLabel: 'Sem etiqueta' },
    board: { moveTo: 'Mover para…', dropRejected: 'Esse movimento não é permitido: {{reason}}', productionHint: 'Trabalho de produção só fica Concluído com implantações verificadas.' },
    create: {
      title: 'Novo item de trabalho',
      responsibility: 'Responsável',
      team: 'Time',
      personal: 'Pessoal (eu)',
      project: 'Projeto',
      noProject: 'Sem projeto',
      type: 'Tipo',
      policy: 'Conclusão',
      description: 'Descrição',
      criteria: 'Critérios de aceite',
      addCriterion: 'Adicionar critério',
      criterionPlaceholder: 'Resultado observável…',
      assignee: 'Responsável',
      labels: 'Etiquetas',
      labelsPlaceholder: 'separadas, por vírgula',
      startDate: 'Data de início',
      dueDate: 'Prazo',
      created: '{{key}} criado',
    },
    provisional: {
      confirm: 'Confirmar como trabalho novo',
      merge: 'Mesclar com trabalho existente',
      mergeTitle: 'Mesclar com trabalho existente',
      mergeHint: 'A chave vira um alias do item escolhido. Contribuições, sessões, pull requests e vínculos com solicitações são transferidos; nada é apagado.',
      mergeInto: 'Mesclar com {{key}}',
      confirmed: 'Confirmado como trabalho novo',
      merged: 'Mesclado com {{key}}',
    },
    detail: {
      overview: 'Visão geral',
      activity: 'Atividade',
      progress: 'Progresso',
      delivery: 'Entrega',
      code: 'Código',
      codeTab: {
        pullRequests: 'Pull requests',
        commits: 'Commits',
        evidence: 'Links reportados por agentes',
        noPullRequests: 'Nenhum pull request vinculado.',
        noCommits: 'Nenhum commit vinculado.',
        linkLabel: 'Vincular um commit ou pull request',
        linkHint: 'Cole a URL do GitHub. Commits e pull requests que mencionam {{key}} se vinculam sozinhos.',
        link: 'Vincular',
        linkedCommit: 'Commit vinculado',
        linkedPr: 'Pull request vinculado',
        unlink: 'Desvincular',
        unlinked: 'Vínculo removido',
        viaKey: 'Pela chave',
        manual: 'Vinculado manualmente',
        prState: {
          OPEN: 'Aberto',
          CLOSED: 'Fechado',
          MERGED: 'Mesclado',
        },
        errors: {
          not_connected: 'Esse repositório não está conectado. Instale primeiro a GitHub App nele.',
          not_synced: 'Esse pull request ainda não foi sincronizado. Associe o repositório a um projeto e tente de novo em alguns minutos.',
          not_found: 'Não existe um commit com esse SHA no repositório.',
          unsupported: 'Cole a URL de um commit ou pull request do GitHub.',
          invalid_format: 'Cole a URL de um commit ou pull request do GitHub.',
        },
      },
      history: 'Histórico',
      description: 'Descrição',
      noDescription: 'Ainda sem descrição.',
      criteria: 'Critérios de aceite',
      noCriteria: 'Sem critérios de aceite.',
      properties: 'Propriedades',
      owner: 'Dono',
      reviewer: 'Revisor',
      repositories: 'Repositórios',
      noRepositories: 'Sem repositórios — tudo bem para trabalho sem código.',
      labels: 'Etiquetas',
      blockers: 'Bloqueios',
      addBlocker: 'Adicionar bloqueio',
      blockerReason: 'O que está bloqueando?',
      preventsCompletion: 'Impede a conclusão',
      resolveBlocker: 'Resolver',
      relations: 'Trabalho relacionado',
      addRelation: 'Vincular trabalho',
      relationKinds: { PARENT_OF: 'Pai de', DEPENDS_ON: 'Depende de', RELATES_TO: 'Relacionado a', DUPLICATES: 'Duplica' },
      requiresCompletion: 'Necessário para concluir',
      contributors: 'Contribuidores',
      linkedRequests: 'Solicitações',
      outcome: 'Resultado',
      outcomePlaceholder: 'O que foi entregue?',
      complete: 'Concluir entregável',
      criterionStatus: { OPEN: 'Aberto', MET: 'Atendido', NOT_MET: 'Não atendido', WAIVED: 'Dispensado' },
      episodes: 'Episódios de conclusão',
      provisionalNote: 'Criado provisoriamente por um agente; confirme ou mescle.',
      blockedNote: 'Bloqueado',
      awaitingDeployment: 'Aguardando verificação da implantação',
      sessions: 'Sessões de agentes',
      evidence: 'Evidências',
      noEvidence: 'Nenhuma evidência anexada.',
      archivedBanner: 'Arquivado {{time}}. Restaure para fazer alterações.',
      archive: 'Arquivar',
      unarchive: 'Restaurar',
      archivedToast: 'Item de trabalho arquivado',
      restoredToast: 'Item de trabalho restaurado',
      delete: 'Excluir definitivamente',
      deleteTitle: 'Excluir {{key}} definitivamente?',
      deleteHint: 'Remove o item de trabalho com seus comentários, anexos, traduções e progresso. O registro de auditoria guarda um registro com o seu motivo. Não pode ser desfeito.',
      deleteConfirm: 'Digite {{key}} para confirmar',
      deletedToast: '{{key}} excluído',
      addSubItem: 'Adicionar subitem',
      subItemOf: 'Subitem de {{key}}',
      copyBranch: 'Copiar nome da branch',
      branchCopied: 'Nome da branch copiado: {{branch}}',
      copyKey: 'Copiar chave',
      keyCopied: '{{key}} copiada',
      editLabels: 'Editar etiquetas',
      labelsPlaceholder: 'Digite uma etiqueta e pressione Enter',
      noLabels: 'Sem etiquetas',
      shortcuts: { title: 'Atalhos de teclado', priority: 'Alterar prioridade', assign: 'Alterar responsável', assignMe: 'Atribuir a mim', labels: 'Editar etiquetas', copyKey: 'Copiar chave' },
      startDate: 'Data de início',
      dueDate: 'Prazo',
      overdue: 'Atrasado',
      dueToday: 'Vence hoje',
      clearDate: 'Remover data',
      subItems: 'Subitens',
      noSubItems: 'Ainda sem subitens.',
      unlinkSubItem: 'Remover dos subitens',
      parents: 'Itens pai',
      progressOf: '{{done}} de {{total}} concluídos',
      timelineEmpty: 'Ainda sem histórico.',
    },
  },
  projects: {
    automation: {
      title: 'Regras automáticas',
      hint: 'Aplicadas a cada hora ao trabalho deste projeto. Deixe um campo vazio para desligar a regra.',
      staleAfter: 'Sem atividade por (dias)',
      staleAction: 'Então',
      actions: { NOTIFY: 'Avisar o responsável', CANCEL: 'Cancelar (o que uma solicitação exige só recebe aviso)' },
      autoArchive: 'Arquivar o concluído após (dias)',
      autoArchiveHint: 'O concluído ou cancelado sai das listas; continua encontrável em Arquivados.',
      off: 'Desligada',
    },
    updates: {
      title: 'Status do projeto',
      statusLabel: 'Status do projeto',
      status: { ON_TRACK: 'No caminho', AT_RISK: 'Em risco', OFF_TRACK: 'Fora do caminho' },
      placeholder: 'O que mudou, o que está em risco, o que vem a seguir?',
      post: 'Publicar status',
      posted: 'Status publicado',
      empty: 'Ainda não há atualizações de status.',
      edited: 'editado',
      reply: 'Responder',
      replyPlaceholder: 'Escreva uma resposta…',
    },
    addManager: 'Adicionar gestor',
    addTeam: 'Adicionar time responsável',
    archive: 'Arquivar este projeto',
    archiveHint: 'Projetos arquivados saem de listas e seletores; seus trabalhos e histórico continuam visíveis.',
    title: 'Projetos',
    subtitle: 'Projetos agrupam trabalho; repositórios são opcionais.',
    new: 'Novo projeto',
    empty: 'Ainda não há projetos.',
    key: 'Chave',
    name: 'Nome',
    description: 'Descrição',
    managers: 'Gestores',
    owningTeams: 'Times responsáveis',
    openWork: '{{count}} abertos',
    defaultPolicy: 'Conclusão padrão',
    requiresReview: 'Exigir revisão independente para entregáveis',
    settings: 'Configurações',
    visibility: 'Visibilidade',
    repositories: 'Repositórios',
  },
  teams: {
    title: 'Times',
    members: 'Membros',
    manager: 'Gestor',
    member: 'Membro',
    addMember: 'Adicionar membro',
    remove: 'Remover',
    new: 'Novo time',
    slug: 'Identificador',
  },
  visibility: {
    title: 'Visibilidade',
    grants: 'Pessoas, times e grupos',
    addGrant: 'Adicionar acesso',
    searchPrincipal: 'Busque pessoas, times ou grupos',
    inherit: 'Restrições dos projetos pais sempre se aplicam.',
    changed: 'Visibilidade atualizada',
  },
  settings: {
    title: 'Configurações',
    profile: 'Perfil',
    displayName: 'Nome de exibição',
    language: 'Idioma',
    timezone: 'Fuso horário',
    notifications: 'Notificações',
    identities: 'Contas vinculadas',
    noIdentities: 'Nenhuma conta vinculada.',
    roles: 'Acesso',
    basic: 'Acesso básico',
    github: {
      addLabel: 'Adicionar uma conta do GitHub',
      hint: 'Seu usuário do GitHub, por exemplo uma conta pessoal com a qual você às vezes faz commits. Contas que fazem commits com seu e-mail da Vammo são vinculadas automaticamente.',
      placeholder: 'usuário',
      add: 'Adicionar',
      added: 'Conta do GitHub adicionada',
      remove: 'Remover',
      removed: 'Conta do GitHub removida',
      confirmRemove: 'Remover esta conta?',
      method: { self: 'Adicionada por você', email: 'Verificada pelo e-mail do commit', admin: 'Vinculada por um administrador', other: 'Vinculada' },
      errors: {
        notFound: 'Nenhuma conta do GitHub tem esse usuário.',
        notUser: 'Só é possível adicionar contas pessoais do GitHub, não organizações nem bots.',
        taken: 'Esta conta do GitHub já está vinculada a outra pessoa. Peça a um administrador.',
        tooMany: 'Você já tem o número máximo de contas do GitHub.',
        unavailable: 'Não foi possível contatar o GitHub. Tente novamente em instantes.',
      },
    },
  },
  admin: {
    title: 'Administração',
    users: 'Usuários e papéis',
    groups: 'Grupos de visibilidade',
    integrations: 'Integrações',
    exceptions: 'Exceções',
    imports: 'Importação do Jira',
    audit: 'Registro de auditoria',
    operations: 'Operações',
    roles: 'Papéis',
    capabilities: 'Capacidades',
    status: 'Status',
    suspend: 'Suspender',
    reactivate: 'Reativar',
    lastLogin: 'Último login',
    changeAccess: 'Alterar acesso',
    reasonRequired: 'Um motivo é registrado no log de auditoria.',
    reason: { title: 'Registrar um motivo', label: 'Motivo', hint: 'O motivo fica no registro de auditoria.', confirm: 'Confirmar' },
    integ: { lastSuccess: 'Último sucesso', lastAttempt: 'Última tentativa', lastError: 'Último erro', circuit: 'Circuito aberto até', cursor: 'Cursor', probe: 'Testar', reconcile: 'Reconciliar', reconcileQueued: 'Reconciliação enfileirada', liveVerified: 'Verificado ao vivo', health: { HEALTHY: 'Saudável', DEGRADED: 'Degradado', FAILING: 'Falhando', DISABLED: 'Desativado', UNVERIFIED: 'Não verificado' }, modes: { live: 'Ao vivo', fixture: 'Fixture', disabled: 'Desativado' } },
    deliveries: { title: 'Entregas de notificações', hint: 'Um envio incerto pode já ter chegado à pessoa. Verifique a evidência do provedor antes de tentar de novo.', filter: 'Status da entrega', filters: { DELIVERY_UNKNOWN: 'Incertas ou com falha', BOUNCED: 'Devolvidas ou suprimidas', SENT: 'Enviadas', ALL: 'Todas' }, empty: 'Nenhuma entrega precisa de atenção.', attempts: '{{count}} tentativa(s)', retry: 'Tentar novamente', retryTitle: 'Tentar esta entrega novamente', retryHint: 'A mensagem pode ser enviada de novo. O motivo fica no registro de auditoria.', retried: 'Nova tentativa na fila' },
    services: { title: 'Serviços e ambientes', hint: 'Aprove quais repositórios constroem cada serviço e quais ambientes são produção. A cobertura usa apenas mapeamentos aprovados.', empty: 'Nenhum serviço descoberto ainda.', repositories: 'Repositórios', environments: 'Ambientes', buildPath: 'Caminho de build (opcional)', buildPathFor: 'Caminho de build de {{name}}', classificationFor: 'Classificação de {{name}}', noRepositories: 'Nenhum repositório mapeado.', approve: 'Aprovar mapeamento', approved: 'Mapeamento aprovado', classes: { PRODUCTION: 'Produção', STAGING: 'Staging', DEVELOPMENT: 'Desenvolvimento', PREVIEW: 'Preview', OTHER: 'Outro' } },
    identities: { action: 'Contas vinculadas', title: 'Contas vinculadas', hint: 'Vincule uma conta do GitHub ou Jira apenas quando tiver confirmado que pertence a esta pessoa. Os vínculos são auditados.', none: 'Nenhuma conta vinculada.', add: 'Vincular uma conta', provider: 'Provedor', search: 'Buscar por usuário ou ID', candidates: 'Contas que podem ser vinculadas', historical: 'Histórico do Jira', unmapped: 'Vista {{count}} vez(es)', noCandidates: 'Nenhuma conta sem vínculo encontrada.', link: 'Vincular conta', linked: 'Conta vinculada', unlink: 'Desvincular' },
    exc: { empty: 'Nenhuma exceção aberta.', resolve: 'Resolver', ignore: 'Ignorar', open: 'Abrir', occurrences: '{{count}} ocorrência(s)', filterKind: 'Tipo', all: 'Todos os tipos', kinds: { UNMATCHED_WORK: 'Trabalho sem associação', UNCERTAIN_ASSOCIATION: 'Associação incerta', UNMAPPED_IDENTITY: 'Identidade sem mapeamento', UNMAPPED_REPOSITORY: 'Repositório sem mapeamento', UNMAPPED_SERVICE: 'Serviço sem mapeamento', CONNECTOR_FAILURE: 'Falha de conector', DUPLICATE_CORRECTION: 'Correção de duplicado', WEBHOOK_INTEGRITY: 'Integridade de webhook', DEPLOYMENT_ORDERING: 'Ordem de deploys', DELIVERY_UNKNOWN: 'Entrega incerta', IMPORT_EXCEPTION: 'Exceção de importação', ACL_QUARANTINE: 'Quarentena de acesso', SCOPE_REVIEW: 'Revisão de escopo', COMPLETION_BLOCKED: 'Conclusão bloqueada' } },
    ops: { noAlerts: 'Sem alertas ativos', outbox: 'Outbox pendente', worker: 'Worker', healthy: 'Saudável', stale: 'Sem heartbeat', aiTokens: 'Tokens de IA hoje', queue: 'Fila', queued: 'Na fila', active: 'Ativos', oldest: 'Espera mais antiga', dead: 'Dead letters', replay: 'Reprocessar', deadEmpty: 'Nenhum job em dead letter.' },
    imp: { mode: 'Modo', projects: 'Chaves de projeto (separadas por vírgula; vazio usa os projetos aprovados)', start: 'Iniciar', queued: 'Importação enfileirada', resume: 'Retomar', cancel: 'Cancelar', empty: 'Nenhuma execução de importação ainda.', exceptions: '{{count}} exceção(ões)', mapping: 'Mapeamentos', mappingHelp: 'Execute a descoberta primeiro. Aprove cada projeto antes de importar amostra, completo ou delta. Níveis de segurança sem mapeamento ficam em quarentena para administradores.', save: 'Salvar mapeamentos', saved: 'Mapeamentos salvos', approved: 'Aprovado', targetProject: 'Projeto de destino', targetTeam: 'Time de triagem/responsável', none: 'Nenhum', requestTypes: 'Tipos importados como solicitações', statusMap: 'Mapeamento de status', securityLevels: 'Níveis de segurança', securityGroup: 'Visível para o grupo', quarantine: 'Quarentena (só administradores)', reconciliation: 'Reconciliação', modes: { DISCOVERY: 'Descoberta', DRY_RUN: 'Simulação', SAMPLE: 'Amostra', FULL: 'Importação completa', DELTA: 'Delta', RECONCILE: 'Reconciliar' }, statuses: { QUEUED: 'Na fila', RUNNING: 'Em execução', PAUSED: 'Pausada', SUCCEEDED: 'Concluída', FAILED: 'Falhou', CANCELED: 'Cancelada' } },
    slackDest: { title: 'Destinos de progresso no Slack', help: 'Canais internos aprovados que recebem o progresso de um projeto ou time. Itens restritos só publicam um link, a menos que o destino seja aprovado para conteúdo restrito e todos os membros sejam membros plenos do workspace.', scope: 'Escopo', project: 'Projeto', team: 'Time', workspace: 'ID do workspace', channel: 'ID do canal', restricted: 'Aprovado para conteúdo restrito', milestones: 'Marcos', add: 'Salvar destino', remove: 'Remover', empty: 'Nenhum destino configurado.', saved: 'Destino salvo', milestoneNames: { REQUEST_RECEIVED: 'Solicitação recebida', REQUEST_ACCEPTED: 'Solicitação aceita', REQUEST_REJECTED: 'Solicitação rejeitada', REQUEST_RESOLVED: 'Solicitação resolvida', REQUEST_NEEDS_ATTENTION: 'Precisa de atenção', WORK_STARTED: 'Trabalho iniciado', WORK_BLOCKED: 'Trabalho bloqueado', WORK_DELIVERED: 'Trabalho entregue' } },
  },
  reports: {
    notes: {
      sourceNotConnected: '{{source}} não está conectado; seus valores são desconhecidos, não zero.',
      sourceNotLive: 'Os dados de {{source}} vêm do modo {{mode}}, não são evidência real.',
      sourceNeverSynced: '{{source}} ainda não sincronizou com sucesso.',
      duplicatesIncluded: '{{count}} destas solicitações estão marcadas como duplicatas.',
      reconsideredExcluded: '{{count}} decisões reconsideradas no período ficam fora da taxa.',
      generationsAcrossRequests: '{{generations}} gerações de atendimento em {{requests}} solicitações distintas.',
      mixedBases: 'Bases de conclusão misturadas ({{bases}}); escolha uma para comparar coisas equivalentes.',
      partialCohorts: 'A janela de acompanhamento vai de cada primeira conclusão até agora; coortes recentes são parciais.',
      currentState: 'Estado atual no momento da consulta; estados passados não são reconstruídos para esta métrica.',
      rolledBack: '{{count}} implantações revertidas aparecem separadas.',
      noIncidentSource: 'Nenhuma fonte de incidentes está conectada; o tempo de recuperação fica indisponível e nunca é inferido das implantações.',
      activityNotProductivity: 'Contribuições são indicadores de atividade, não uma medida de produtividade.',
      prAttribution: 'A autoria é indicada por pull request, não por linha. Exclusões de arquivos gerados só valem com dados completos de arquivos.',
      captureDenominator: 'Denominador: pull requests observados em repositórios vinculados a projetos; trabalho fora da plataforma não tem denominador.',
      providerErrors: '{{count}} chamadas ao provedor falharam.',
    },
    filters: { any: 'Qualquer', source: 'Origem', mine: 'Só minhas contribuições' },
    chartLabel: 'Gráfico de {{metric}} por grupo',
    title: 'Relatórios',
    subtitle: 'Métricas calculadas a partir de fatos registrados, com o seu acesso aplicado. Dados desconhecidos nunca aparecem como zero.',
    metric: 'Métrica',
    period: 'Período',
    groupBy: 'Agrupar por',
    basis: 'Base de conclusão',
    anyBasis: 'Todas as bases',
    firstOnly: 'Só a primeira conclusão',
    run: 'Executar',
    export: 'Exportar CSV',
    exporting: 'Preparando exportação…',
    download: 'Baixar CSV',
    total: 'Total',
    count: 'Quantidade',
    unavailable: 'Indisponível',
    median: 'Mediana',
    p85: 'Percentil 85',
    coverage: 'Cobertura',
    overlapping: 'Os grupos se sobrepõem: uma tarefa pode aparecer em vários grupos, então as linhas não somam o total.',
    empty: 'Sem dados neste período.',
    tierLabel: 'Nível',
    formula: 'Fórmula v{{version}}',
    watermark: 'Dados de {{time}}',
    definition: 'Definição',
    last7: 'Últimos 7 dias',
    last30: 'Últimos 30 dias',
    last90: 'Últimos 90 dias',
    thisYear: 'Este ano',
    from: 'De',
    to: 'Até',
    group: 'Grupo',
    value: 'Valor',
    ratio: '{{num}} de {{den}}',
    tiers: { GENERAL: 'Geral', TECHNICAL: 'Técnico', SENSITIVE: 'Sensível' },
    coverageStatus: { COMPLETE: 'Completa', PARTIAL: 'Parcial', UNKNOWN: 'Desconhecida' },
    groups: { none: 'Sem agrupamento', team: 'Time', project: 'Projeto', type: 'Tipo', priority: 'Prioridade', week: 'Semana', month: 'Mês', repository: 'Repositório', service: 'Serviço', person: 'Pessoa', outcome: 'Resultado', source: 'Origem' },
    metrics: {
      requests_created: { name: 'Solicitações criadas', help: 'Solicitações enviadas pela data de envio.' },
      request_first_response: { name: 'Tempo até a primeira resposta', help: 'Do envio até a primeira decisão humana de triagem.' },
      request_acceptance_rate: { name: 'Taxa de aceitação', help: 'Primeiras aceitações sobre aceitas mais rejeitadas.' },
      requests_fulfilled: { name: 'Solicitações resolvidas', help: 'Solicitações distintas resolvidas no período.' },
      request_lead_time: { name: 'Lead time da solicitação', help: 'Do envio até a resolução, por resolução.' },
      work_completed: { name: 'Trabalho concluído', help: 'Itens de trabalho distintos concluídos no período.' },
      work_cycle_time: { name: 'Tempo de ciclo', help: 'Do início à conclusão de cada episódio.' },
      work_in_progress: { name: 'Trabalho em andamento', help: 'Itens em andamento, revisão ou integrados agora.' },
      blocked_time: { name: 'Tempo bloqueado', help: 'União dos intervalos bloqueados por item no período.' },
      review_wait: { name: 'Espera por revisão', help: 'Do pedido de revisão até a decisão.' },
      integration_to_production: { name: 'Da integração à produção', help: 'Da integração até a entrega verificada em produção.' },
      deployment_frequency: { name: 'Frequência de deploy', help: 'Deploys bem-sucedidos em produção.' },
      deployment_failure_rate: { name: 'Taxa de falha de deploy', help: 'Falhos sobre bem-sucedidos mais falhos em produção.' },
      reopen_rate: { name: 'Taxa de reabertura', help: 'Trabalho concluído que depois foi reaberto.' },
      recovery_time: { name: 'Tempo de recuperação', help: 'Requer evidência de incidentes; indisponível sem uma fonte.' },
      contribution_activity: { name: 'Contribuições', help: 'Contribuições observadas de implementação, revisão e outras.' },
      code_additions: { name: 'Linhas adicionadas', help: 'Adições reportadas pelo provedor em PRs mesclados.' },
      code_deletions: { name: 'Linhas removidas', help: 'Remoções reportadas pelo provedor em PRs mesclados.' },
      capture_coverage: { name: 'Cobertura de captura', help: 'PRs observados vinculados a trabalho confirmado.' },
      requester_confirmation: { name: 'Confirmação do solicitante', help: 'Confirmado, persiste ou sem resposta por resolução.' },
      integrated_ai_usage: { name: 'Uso de IA', help: 'Tokens reportados pelo provedor para tradução e intake.' },
    },
  },
  attachments: {
    title: 'Arquivos',
    add: 'Anexar arquivos',
    empty: 'Nenhum arquivo anexado ainda.',
    uploading: 'Enviando {{name}}…',
    scanning: 'Verificando malware',
    rejected: 'Rejeitado',
    download: 'Baixar',
    remove: 'Remover arquivo',
    removed: 'Arquivo removido',
    tooLarge: '{{name}} tem mais de {{max}} MiB.',
    tooMany: 'Você pode anexar no máximo {{max}} arquivos por vez.',
    uploaded: '{{count}} arquivo(s) enviado(s); ficam disponíveis após a verificação de malware.',
    pendingNote: 'Os arquivos ficam em quarentena até a verificação terminar.',
    video: 'Vídeo',
    view: 'Ver {{name}}',
    viewer: 'Visualizador de arquivos',
    viewerHint: 'Use as setas para passar de um arquivo a outro e Esc para fechar.',
    counter: '{{index}} de {{total}}',
    previous: 'Arquivo anterior',
    next: 'Próximo arquivo',
    actualSize: 'Tamanho real',
    fitToScreen: 'Ajustar à tela',
    openInNewTab: 'Abrir em uma nova aba',
    loadFailed: 'Este arquivo não pode ser exibido aqui. Baixe-o para abri-lo no seu dispositivo.',
  },
  savedViews: {
    title: 'Visões',
    mine: 'Minhas visões',
    sharedWithEveryone: 'Visões compartilhadas',
    shared: 'Compartilhada',
    empty: 'Nenhuma visão salva ainda.',
    saveCurrent: 'Salvar filtros atuais…',
    name: 'Nome da visão',
    shareLabel: 'Compartilhar com todos (só os filtros; cada pessoa continua vendo apenas o que pode acessar)',
    hint: 'Uma visão salva guarda os filtros, nunca os resultados.',
    saved: 'Visão salva',
    deleted: 'Visão excluída',
    delete: 'Excluir visão',
  },
  slack: {
    modalTitle: 'Criar solicitação Vammo',
    reviewTitle: 'Revisar solicitação',
    next: 'Próximo',
    submit: 'Enviar',
    cancel: 'Cancelar',
    close: 'Fechar',
    privacyNote: 'Só você vê este formulário. A solicitação é privada até você enviá-la.',
    fields: { title: 'Título curto', problem: 'O que está acontecendo?', expected: 'O que deveria acontecer?', impact: 'Quem é afetado e quanto? (deixe vazio se não souber)' },
    impactUnknown: 'Desconhecido por enquanto',
    duplicates: 'Solicitações possivelmente relacionadas',
    submitted: 'Solicitação *{{key}}* enviada. Acompanhe aqui: {{url}}',
    dmHelp: 'Escreva numa mensagem o que você precisa e eu transformo num rascunho de solicitação Vammo que você revisa antes de enviar. Você também pode usar /vammo-work ou o atalho "Create Vammo request" em qualquer mensagem.',
    dmDraftReady: 'Preparei um rascunho de solicitação: *{{title}}*. Revise, complete e envie:',
    dmReview: 'Revisar e enviar',
    linkPrompt: 'Para criar solicitações pelo Slack, vincule sua conta do Slack à sua conta Vammo (link privado, válido por 10 minutos): {{url}}',
    notLinked: 'Sua conta do Slack ainda não está vinculada. Execute /vammo-work de novo.',
    expired: 'Este rascunho não está mais disponível. Comece de novo com /vammo-work.',
    required: 'Obrigatório',
    unavailable: 'Não foi possível abrir o formulário. Tente de novo ou use o app web.',
    progress: '*{{key}}* {{title}} — {{status}}\n{{url}}',
    progressLinkOnly: 'Um item acompanhado foi atualizado. Abra no Vammo Work Tracker: {{url}}',
    linkTitle: 'Vincular conta do Slack',
    linkBody: 'Vincular o usuário do Slack {{handle}} à sua conta Vammo? As solicitações criadas pelo Slack serão registradas em seu nome.',
    linkConfirm: 'Vincular conta',
    linkDone: 'Sua conta do Slack foi vinculada. Pode fechar esta aba e executar /vammo-work de novo.',
    linkInvalid: 'Este link expirou ou já foi usado. Execute /vammo-work no Slack para obter um novo.',
  },
  notifications: {
    preferences: {
      title: 'Configurações de notificação',
      event: 'Evento',
      email: 'E-mail',
      browser: 'Navegador',
      immediate: 'Na hora',
      digest: 'Resumo diário',
      muted: 'Desativado',
      on: 'Ativado',
      off: 'Desativado',
    },
    events: {
      REQUEST_RECEIVED: 'Recebemos sua solicitação',
      REQUEST_NEEDS_INFORMATION: 'Precisamos de mais informações',
      REQUEST_ACCEPTED: 'Sua solicitação foi aceita',
      REQUEST_REJECTED: 'Sua solicitação não foi aceita',
      REQUEST_WORK_STARTED: 'O trabalho na sua solicitação começou',
      REQUEST_RESOLVED: 'Sua solicitação foi resolvida',
      REQUEST_NEEDS_ATTENTION: 'Uma solicitação precisa de atenção',
      WORK_ASSIGNED: 'Trabalho atribuído a você',
      WORK_BLOCKED: 'Algo que você segue está bloqueado',
      COMMENT_ADDED: 'Novo comentário em algo que você segue',
      MENTIONED: 'Você foi mencionado',
      WORK_DUE_SOON: 'Um trabalho seu vence em breve',
      WORK_OVERDUE: 'Um trabalho seu está atrasado',
      WORK_STALE: 'Um trabalho seu está sem atividade',
    },
    browser: {
      title: 'Notificações do navegador',
      enable: 'Ativar neste navegador',
      disable: 'Desativar neste navegador',
      test: 'Enviar um teste',
      enabled: 'As notificações do navegador estão ativadas',
      disabled: 'As notificações estão desativadas neste navegador',
      testSent: 'Notificação de teste enviada',
      testNone: 'Nenhum navegador aceitou o teste. Desative e ative as notificações de novo.',
      blockedHelp: 'As notificações estão bloqueadas para este site. Permita-as nas configurações do site no navegador (o ícone ao lado do endereço).',
      devices: 'Navegadores que recebem notificações',
      unknownDevice: 'Navegador',
      forget: 'Esquecer',
      state: {
        unsupported: 'Este navegador não consegue mostrar notificações. No iPhone e iPad, adicione o tracker à Tela de Início primeiro.',
        blocked: 'As notificações estão bloqueadas para este site. Permita-as nas configurações do site no navegador e recarregue a página.',
        off: 'Receba atribuições, menções e novidades do que você segue, mesmo com o tracker fechado.',
        on: 'Este navegador mostra suas notificações.',
      },
    },
  },
  push: {
    testTitle: 'As notificações estão ativadas',
    testBody: 'Este navegador mostrará suas notificações do Vammo Work Tracker.',
  },
  email: {
    greeting: 'Olá {{name}},',
    open: 'Abrir no Vammo Work Tracker',
    footer: 'Você recebe isto porque participa deste registro. Altere as notificações no seu perfil.',
    actionConfirm: 'Sim, está resolvido',
    actionPersists: 'Não, ainda acontece',
    actionComment: 'Deixar um comentário',
    confirmHint: 'Diga ao time se funcionou para você: abra a solicitação e escolha "Sim, está resolvido" ou "Não, ainda acontece". Abrir este e-mail ou link nunca registra uma resposta por si só.',
    digestSubject: 'Seu resumo do Vammo Work Tracker',
    layout: {
      kindRequest: 'Solicitação',
      kindWork: 'Trabalho',
      openRequest: 'Abrir a solicitação',
      openWork: 'Abrir o trabalho',
      openMyWork: 'Abrir Meu trabalho',
      manageNotifications: 'Gerenciar suas notificações',
      actionNeeded: 'Requer ação',
      update: 'Novidade',
      digestHeadline: 'Seu resumo do dia',
      digestIntro: 'Veja o que aconteceu nos registros que você segue ({{count}}).',
    },
    events: {
      REQUEST_RECEIVED: { subject: '[{{key}}] Recebemos sua solicitação', headline: 'Recebemos sua solicitação', body: 'Sua solicitação "{{title}}" foi recebida e aguarda triagem.' },
      REQUEST_NEEDS_INFORMATION: { subject: '[{{key}}] Precisamos de mais informações', headline: 'O time precisa de mais informações', body: 'O time precisa de mais informações sobre "{{title}}".' },
      REQUEST_ACCEPTED: { subject: '[{{key}}] Sua solicitação foi aceita', headline: 'Sua solicitação foi aceita', body: 'Sua solicitação "{{title}}" foi aceita. Aceitar não é uma data de entrega; você será avisado quando o trabalho começar.' },
      REQUEST_REJECTED: { subject: '[{{key}}] Sua solicitação não foi aceita', headline: 'Sua solicitação não foi aceita', body: 'Sua solicitação "{{title}}" não foi aceita. Abra-a para ler o motivo.' },
      REQUEST_WORK_STARTED: { subject: '[{{key}}] O trabalho começou', headline: 'O trabalho na sua solicitação começou', body: 'O trabalho em "{{title}}" começou.' },
      REQUEST_RESOLVED: { subject: '[{{key}}] Resolvida — confirme, por favor', headline: 'Sua solicitação foi resolvida', body: 'O time resolveu "{{title}}".' },
      REQUEST_NEEDS_ATTENTION: { subject: '[{{key}}] Requer atenção', headline: 'Uma solicitação precisa da sua atenção', body: 'O solicitante informa que "{{title}}" ainda acontece, ou a entrega necessária foi perdida.' },
      WORK_ASSIGNED: { subject: '[{{key}}] Atribuído a você', headline: 'Novo trabalho atribuído a você', body: '"{{title}}" foi atribuído a você.' },
      WORK_BLOCKED: { subject: '[{{key}}] Bloqueado', headline: 'Algo que você segue está bloqueado', body: '"{{title}}" está bloqueado.' },
      WORK_DONE: { subject: '[{{key}}] Concluído', headline: 'Algo que você segue foi concluído', body: '"{{title}}" foi concluído.' },
      COMMENT_ADDED: { subject: '[{{key}}] Novo comentário', headline: 'Novo comentário', body: 'Há um novo comentário em "{{title}}".' },
      MENTIONED: { subject: '[{{key}}] Você foi mencionado', headline: 'Você foi mencionado', body: 'Você foi mencionado em "{{title}}".' },
      WORK_DUE_SOON: { subject: '[{{key}}] Vence em breve', headline: 'O trabalho vence em breve', body: '"{{title}}" vence em breve.' },
      WORK_OVERDUE: { subject: '[{{key}}] Atrasado', headline: 'O trabalho está atrasado', body: '"{{title}}" passou da data de entrega.' },
      WORK_STALE: { subject: '[{{key}}] Sem atividade recente', headline: 'Trabalho sem atividade recente', body: '"{{title}}" não muda há algum tempo. Atualize ou feche se não for mais necessário.' },
    },
  },
  tutorial: {
    title: 'Tutorial',
    subtitle: 'Conecte seu assistente de IA, peça o que você precisa, planeje o trabalho com sua equipe e deixe o trabalho de código ser registrado sozinho.',
    toc: 'Nesta página',
    copy: 'Copiar',
    copied: 'Copiado',
    copyFailed: 'Não foi possível copiar; selecione o texto e copie manualmente.',
    footer: 'Tudo o que você ou seu assistente fazem aqui roda com as suas próprias permissões.',
    connect: {
      title: 'Conecte seu assistente de IA',
      intro: 'O tracker é um servidor MCP. Depois de conectado, seu assistente pode abrir solicitações, criar e atualizar trabalho e registrar o que faz, sempre como você e com as suas permissões. Você entra com sua conta Vammo; não há nenhuma chave de API para copiar.',
      serverUrl: 'URL do servidor',
      claudeDesktop: {
        name: 'Claude Desktop',
        summary: 'O app do Claude para Windows e macOS, e o claude.ai na web.',
        s1: 'Abra **Settings → Connectors** e escolha **Add custom connector**.',
        s2: 'Dê o nome `Vammo Work Tracker` e cole a URL do servidor:',
        s3: 'Escolha **Connect**. O login da Vammo abre no navegador; entre e aprove o acesso solicitado.',
        s4: 'Em um chat novo, abra o menu de ferramentas (**+**) e confira se **Vammo Work Tracker** está ativado.',
        s5: 'Opcional: cole estas instruções em um projeto do Claude para que ele sempre trabalhe com o tracker:',
        instructions: 'Use o conector Vammo Work Tracker para solicitações e trabalho. Chame get_context primeiro. Para abrir uma solicitação, chame prepare_request com o que eu descrever, mostre o rascunho e os campos que faltam, e só chame submit_request depois que eu confirmar. Quando uma solicitação minha for resolvida, pergunte se ficou resolvida e registre confirm_request. Nunca invente fatos, prioridades ou status de deploy.',
      },
      claudeCode: {
        name: 'Claude Code (terminal)',
        summary: 'O Claude no seu terminal, na sua IDE ou na aba Code do app desktop.',
        s1: 'Instale o plugin uma única vez. Ele adiciona o servidor, a skill de acompanhamento e um lembrete, para que o trabalho de código seja registrado automaticamente em todos os seus repositórios:',
        s2: 'Reinicie o Claude Code, execute `/mcp`, selecione `plugin:vammo-work-tracker:vammo` e escolha **Authenticate**. Entre com sua conta Vammo no navegador.',
        s3: 'Você precisa de acesso de leitura ao repositório `leopardaelectric/vammo-work-tracker` no GitHub. Se você configurou o tracker manualmente antes, execute `claude mcp remove vammo` e apague `~/.claude/skills/vammo-work-tracking/`. Para atualizar: `claude plugin update vammo-work-tracker@vammo`.',
      },
      chatgptDesktop: {
        name: 'ChatGPT Desktop',
        summary: 'O app do ChatGPT e o chatgpt.com. Conectores adicionados na web também aparecem no app desktop.',
        s1: 'No ChatGPT abra **Settings → Apps & Connectors → Advanced settings** e ative o **Developer mode**.',
        s2: 'De volta em **Apps & Connectors**, escolha **Create**, dê o nome `Vammo Work Tracker`, escolha autenticação **OAuth** e cole a URL do servidor:',
        s3: 'Escolha **Create**, entre com sua conta Vammo e aprove o acesso solicitado.',
        s4: 'Em um chat, abra o menu **+**, escolha **Developer mode** e selecione o conector da Vammo. O ChatGPT pede confirmação antes de cada alteração que faz.',
        note: 'Os nomes dos menus mudam conforme o plano e a versão do app. Nos planos Business e Enterprise, um administrador do workspace pode precisar habilitar o modo desenvolvedor ou publicar o conector antes.',
      },
      codex: {
        name: 'Codex (ChatGPT terminal)',
        summary: 'A CLI do OpenAI Codex e a extensão do Codex para IDE.',
        s1: 'Instale o mesmo plugin uma única vez; ele adiciona o servidor e a skill de acompanhamento:',
        s2: 'Faça login; o navegador abre o login da Vammo:',
        s3: 'Você precisa de acesso de leitura ao repositório `leopardaelectric/vammo-work-tracker` no GitHub. Se você configurou o tracker manualmente antes, remova o bloco `[mcp_servers.vammo]` de `~/.codex/config.toml` e a pasta antiga da skill `vammo-work-tracking`.',
      },
      verifyTitle: 'Confira a conexão',
      verify: 'Peça ao seu assistente: “Chame get_context no servidor vammo.” Ele deve responder com seu nome e suas permissões. Você continua conectado enquanto usar; só 90 dias sem uso, revogar a conexão ou uma conta suspensa encerram a sessão.',
      adminNote: 'Se a sua organização do Claude ou do ChatGPT bloqueia conectores personalizados, peça ao responsável pelo workspace para liberar esta URL.',
    },
    request: {
      title: 'Peça uma nova funcionalidade',
      intro: 'Qualquer pessoa pode pedir uma funcionalidade ou relatar um problema. Você não precisa saber como construir: descreva o que precisa e por quê. Cada solicitação recebe uma chave `REQ-`.',
      web: { title: 'Pela web', body: 'Escolha **Nova solicitação** ou pressione `R`. A aba **Guiado** faz perguntas em linguagem simples; a aba **Formulário** permite preencher os campos diretamente. Rascunhos ficam privados até você enviar.' },
      assistant: { title: 'Pelo Claude ou ChatGPT', body: 'Diga, por exemplo: “Abra uma solicitação: os pilotos querem ver o histórico de trocas no app.” O assistente prepara um rascunho, mostra o que falta e só envia depois que você confirmar.' },
      slack: { title: 'Pelo Slack', body: 'Execute `/vammo-work` ou use o atalho **Create Vammo request** em qualquer mensagem. Na primeira vez, o Slack envia um link privado para conectar sua conta.' },
      tipsTitle: 'O que faz uma boa solicitação',
      t1: 'O que acontece hoje e o que deveria acontecer.',
      t2: 'Quem é afetado e o quanto isso importa; “ainda não sei” é uma resposta válida.',
      t3: 'Prints, links ou exemplos como anexos.',
      after: 'A triagem aceita a solicitação e a vincula a um trabalho, e você é avisado conforme ela avança. Quando for entregue, você será perguntado se o problema foi resolvido.',
    },
    team: {
      title: 'Crie tarefas para sua equipe',
      intro: 'Itens de trabalho (chaves `VAM-`) são as tarefas que uma equipe executa. Uma solicitação descreve uma necessidade; um item de trabalho descreve o que será feito a respeito.',
      s1: 'Escolha **Novo item de trabalho** ou pressione `C`.',
      s2: 'Escreva um título curto e escolha o tipo: funcionalidade, bug, manutenção, pesquisa etc.',
      s3: 'Em **Responsável**, escolha a equipe (ou **Pessoal** para o seu próprio trabalho). Se quiser, defina uma pessoa, um projeto e uma prioridade.',
      s4: 'Adicione critérios de aceitação: os resultados observáveis que indicam que a tarefa está pronta.',
      s5: 'Para atender uma solicitação, abra-a e escolha **Novo item de trabalho** ali, assim quem pediu acompanha o andamento automaticamente.',
      s6: 'Acompanhe em **Trabalho** como lista ou quadro, filtrando por equipe, projeto ou responsável.',
      assistantTitle: 'Ou peça ao seu assistente',
      assistant: '“Crie uma tarefa para a equipe de Plataforma adicionar um filtro ao histórico de trocas, prioridade alta.” O assistente diz o que vai criar e pede confirmação antes.',
    },
    notifications: {
      title: 'Ative as notificações',
      intro: 'Saiba quando suas solicitações avançam, quando um trabalho é atribuído a você ou quando alguém menciona você, sem deixar o tracker aberto. Tudo também aparece na lista de notificações dentro do tracker.',
      open: 'Configurar notificações',
      s1: 'Abra **Configurações → Notificações**, ou escolha o sino no canto superior direito.',
      s2: 'Em **Notificações do navegador**, escolha **Ativar neste navegador** e aceite quando o navegador pedir permissão.',
      s3: 'Escolha **Enviar um teste** para conferir. Repita em cada computador ou celular onde quiser receber; o mesmo cartão lista seus navegadores para você esquecer os antigos.',
      s4: 'Em **Configurações de notificação**, escolha para cada evento: e-mail **Na hora**, **Resumo diário** ou **Desativado**, e navegador **Ativado** ou **Desativado**.',
      browser: { title: 'Navegador', body: 'Avisos neste dispositivo, mesmo com a aba do tracker fechada. São ativados por navegador e por dispositivo.' },
      email: { title: 'E-mail', body: 'Ativado por padrão para suas solicitações, atribuições e menções. **Resumo diário** junta as novidades do dia em um único e-mail.' },
      slack: { title: 'Slack', body: 'Solicitações abertas pelo Slack recebem o andamento na mesma thread. Administradores podem aprovar um canal por projeto ou equipe para receber as novidades.' },
      troubleTitle: 'Se as notificações não chegarem',
      blocked: 'Se o navegador bloqueou, permita as notificações nas configurações do site (o ícone ao lado do endereço) e recarregue a página.',
      iphone: 'No iPhone ou iPad, primeiro adicione o tracker à Tela de Início (**Compartilhar → Adicionar à Tela de Início**) e ative pelo ícone.',
      missing: 'Se você não vê **Notificações do navegador** em Configurações, elas ainda não estão habilitadas neste ambiente; o e-mail e a lista dentro do app continuam funcionando.',
    },
    tracking: {
      title: 'O trabalho de código é registrado sozinho',
      intro: 'Você não precisa atualizar tickets à mão. Quando você programa com um assistente conectado ou abre pull requests, o tracker registra o trabalho e o vincula à tarefa certa.',
      f1: { title: 'Você começa uma tarefa', body: 'Com a skill instalada, o assistente registra seu objetivo, repositório e branch. Ele reutiliza a tarefa que você indicar (por exemplo `VAM-42`) ou a que já está vinculada à sua branch ou sessão, e só cria uma nova quando nada corresponde.' },
      f2: { title: 'Os marcos são registrados', body: 'Andamento, verificações que ele realmente executou, bloqueios e pull requests são adicionados à tarefa conforme a sessão avança. Um pequeno arquivo dentro de `.git/` guarda o vínculo entre sessões e nunca é commitado.' },
      f3: { title: 'O GitHub confirma', body: 'Um pull request cujo título, branch ou descrição menciona uma chave como `VAM-42`, ou cuja branch corresponde a uma sessão de agente, é vinculado automaticamente. Associe o repositório a um projeto; além dos webhooks, o tracker reconcilia a cada 15 minutos.' },
      f4: { title: 'A entrega é verificada', body: 'Quando todos os pull requests vinculados são mergeados, o trabalho de produção passa para integrado. Ele só chega a **Concluído** quando o tracker observa a mudança implantada em produção, e não porque um PR foi mergeado ou uma sessão terminou.' },
      keyTipTitle: 'Dica: coloque a chave na branch',
      keyTip: 'Uma branch como `feat/VAM-42-swap-history`, ou `VAM-42` no título do pull request, vincula o trabalho com certeza, mesmo sem assistente.',
      ambiguousTitle: 'Quando a correspondência é incerta',
      ambiguous: 'Títulos parecidos são apenas sugestões. Se várias tarefas puderem corresponder, o assistente mostra as candidatas e pergunta. Se o trabalho precisar continuar enquanto isso, ele cria um item provisório que aparece na caixa de exceções para confirmar ou mesclar; nada se perde.',
    },
  },
  time: {
    justNow: 'agora mesmo',
  },
};
